import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  Suspense,
} from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import DateRangePicker from "../../../../components/DateRangePicker";
import { customStyles } from "../../../../assets/table-style";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../../../assets/global/url";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../../../assets/img/NoAccess.png";
import { Modal, Tab, Tabs, Popover } from "react-bootstrap";
import useDecodeToken from "../../../../hooks/customHook/useDecodeToken";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { useValidPage } from "../../../../hooks/customHook/paginationHook/useValidPage";
import { Rss } from "@phosphor-icons/react";
import {
  compactNumberFormat,
  formatToTwoDecimal,
} from "../../../../utils/numberFormatter";
import CurrencySelector from "../../../../components/CurrencySelector";
import { MultiSelect } from "react-multi-select-component";
import { useObserver } from "../../../../hooks/customHook/useObserver";
import PendingStatusTab from "./PendingStatusTab";
import ApprovedStatusTab from "./ApprovedStatusTab";
import ReturnedStatusTab from "./ReturnedStatusTab";
import ExportOptionsModal from "../../../../components/Export/ExportOptionsModal";
import PdfPreviewModal, {
  usePdfPreview,
} from "../../../../components/Export/PdfPreviewModal";
import invoiceExportConfig from "./Export/invoiceExportConfig";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {
  getMonthBoundaries,
  initializeCutoff,
} from "../../../../utils/newdate";
import InvoiceReOpenModal from "./InvoiceReOpenModal";
import FilterStore from "../../../../hooks/customHook/FilterStore";
import { NoTranslate } from "../../../../helper/prevent_translate_browser";
import WidgetInfoIcon from "./WidgetInfoIcon";

const NewInvoice = ({ authrztn }) => {
  const navigate = useNavigate();

  const validtabs = ["pending", "approved"];
  const [activeTab, setActiveTab] = useState(() => {
    const saved = FilterStore.getTab();
    return validtabs.includes(saved) ? saved : "pending";
  });
  const [currencyId, setCurrencyId] = useState(() =>
    FilterStore.getCurrencyId(),
  );

  // useEffect(() => {
  //   FilterStore.saveCurrencyId(currencyId);
  // }, [currencyId]);

  useEffect(() => {
    FilterStore.saveCurrencyId(currencyId);
    if (!isLoading && selectedCutOff?.from) {
      fetchWidgets(selectedCutOff);
      fetchLastCutoffReceivable(selectedCutOff);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyId]);

  const [widgetCurrencySymbol, setWidgetCurrencySymbol] = useState(() =>
    FilterStore.getCurrencyName(),
  );
  useEffect(() => {
    FilterStore.saveCurrencyName(widgetCurrencySymbol);
  }, [widgetCurrencySymbol]);

  const handleTabSelect = (key) => {
    setActiveTab(key);
    FilterStore.saveTab(key);
  };

  const userLoggedID = useDecodeToken();
  const { containerRef, containerItemRefs, visible } = useObserver();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCutOff, setSelectedCutOff] = useState({
    id: "All",
    from: null,
    to: null,
  }); // state for input select

  // For widgets
  const [widget, setWidget] = useState({});

  // For last cutoff receivable modal
  const [show, setShow] = useState(false);
  const [modalSearch, setModalSearch] = useState("");
  const modalSearchRef = useRef(null);
  const handleClose = () => {
    setShow(false);
    setModalSearch("");
  };
  const handleShow = () => setShow(true);

  const lastCutoffReceivablePagination = useServerPagination(
    `${BASE_URL}/invoice/last-cutoff-receivable`,
    10,
  );

  // For last cutoff receivable modal table column
  const modalColumns = [
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_id,
    },
    {
      name: "Destination",
      selector: (row) => row.destination,
    },
    {
      name: "Customer",
      cell: (row) => (
        <NoTranslate>
          {/* Company name first, else first name + last name (last name only if available) */}
          {row.customer.company_name ||
            [row.customer.first_name, row.customer.last_name]
              .filter(Boolean)
              .join(" ")}
        </NoTranslate>
      ),
    },
    {
      name: "Invoice Date",
      selector: (row) => row.invoice_date,
    },
    {
      name: "Due Date",
      selector: (row) => row.daysDifference,
    },
    {
      name: "Currency",
      selector: (row) => row.currency.currency_name,
    },
    {
      name: "Total Amount",
      selector: (row) =>
        formatToTwoDecimal(
          row.total_amount *
            (row.resolved_currency_rate ?? row.currency.currency_rate),
        ),
    },
  ];

  // Initialize date range and load initial data
  const fetchCutOff = () => {
    // If user previously clicked Clear Filter, NIisAllData() is true.
    // Stay in "show all" mode instead of falling back to current month.
    if (FilterStore.NIisAllData()) {
      const allCutoff = { id: "All", from: null, to: null };
      setSelectedCutOff(allCutoff);
      fetchWidgets(allCutoff);
      fetchLastCutoffReceivable(allCutoff);
      // Show default month in picker even in show-all mode
      const boundaries = getMonthBoundaries();
      const defaultCutoff = initializeCutoff(boundaries);
      setDisplayCutOff({ from: defaultCutoff.from, to: defaultCutoff.to });
      setIsLoading(false);
      return;
    }

    const savedFilter = FilterStore.get();

    const initialCutoff =
      savedFilter ??
      (() => {
        const boundaries = getMonthBoundaries();
        return initializeCutoff(boundaries);
      })();

    setSelectedCutOff(initialCutoff);
    setDisplayCutOff({ from: initialCutoff.from, to: initialCutoff.to });
    fetchWidgets(initialCutoff);
    fetchLastCutoffReceivable(initialCutoff);
    setIsLoading(false);
  };

  // For sales invoice widgets
  const fetchWidgets = async (cutoff) => {
    try {
      const res = await axios.get(`${BASE_URL}/invoice/widgets`, {
        params: {
          startDate: cutoff?.from,
          endDate: cutoff?.to,
          currencyId,
        },
      });

      setWidget(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Debounce modal search → push new params into pagination hook
  useEffect(() => {
    const timer = setTimeout(() => {
      // if (!selectedCutOff?.from) return;
      // No from-date guard — null startDate is valid (fetches all records)
      lastCutoffReceivablePagination.updateParams({
        startDate: selectedCutOff?.from ?? null,
        // startDate: selectedCutOff?.from,
        currencyId,
        search: modalSearch.trim(), // always send (empty string clears the filter)
      });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalSearch]);

  // For last cutoff receivable widget's modal
  const fetchLastCutoffReceivable = (cutoff) => {
    lastCutoffReceivablePagination.updateApiUrl(
      `${BASE_URL}/invoice/last-cutoff-receivable`,
    );
    lastCutoffReceivablePagination.updateParams({
      startDate: cutoff?.from,
      currencyId,
    });
  };

  const widgetInfo = {
    "Last Cutoff Receivable": {
      formula: "Σ Debit − Σ Credit (SalesJournal)",
      conditions: [
        "Journal date is before the start of the selected cutoff period",
        "Filtered by selected currency (or all currencies × rate)",
      ],
      description:
        "The outstanding receivable balance carried over before the current cutoff period begins. Think of it as your opening balance.",
    },
    "Current Sales Total": {
      formula: "Σ Debit (SalesJournal)",
      conditions: [
        "Journal date is within the selected date range",
        "Filtered by selected currency",
      ],
      description:
        "Total value of all sales invoiced within the selected period, based on debit entries in the sales journal.",
    },
    "Total Discount": {
      formula: "Σ Discount Amount (SalesInvoice)",
      conditions: [
        "Invoice date is within the selected date range",
        "Status: Approved, Partially Collected, or Collected",
        "Filtered by selected currency",
      ],
      description:
        "Sum of all discounts applied to approved invoices within the period.",
    },
    "Re-Open": {
      formula: "Σ Debit − Σ Credit (SalesJournal, ROT-S entries only)",
      conditions: [
        "Transaction ID starts with 'ROT-S' (e.g. ROT-S-20260410582449)",
        "Journal date is within the selected date range",
        "Filtered by selected currency",
      ],
      description:
        "Net amount of re-opened invoices in the period. A re-open reverses a previously closed invoice, creating new debit/credit journal entries prefixed with ROT-S.",
    },
    "Current Cutoff Receivable": {
      formula: "Σ Debit − Σ Credit (SalesJournal)",
      conditions: [
        "Journal date is within the selected date range",
        "Filtered by selected currency",
      ],
      description:
        "Net receivable balance for the current cutoff period. Represents how much is still outstanding after collections within this period.",
    },
    "Sales Collection": {
      formula: "Σ Total Amount (SalesJournal)",
      conditions: [
        "Payment type = 'Credit'",
        "Date is within the selected date range",
        "Filtered by selected currency",
      ],
      description:
        "Total payments collected from customers within the selected period, recorded as credit entries in the sales journal.",
    },
    "Previous Month Sales Kilo": {
      formula: "Σ Net Weight (SalesInvoiceInventory)",
      conditions: [
        "Invoice date is within the previous cutoff period",
        "Status: Approved, Partially Collected, or Collected",
        "Filtered by selected currency",
      ],
      description:
        "Total weight (in kilograms) of goods sold in the previous cutoff period. Used as a reference baseline against the current month.",
    },
    "Current Month Sales Kilo": {
      formula: "Σ Net Weight (SalesInvoiceInventory)",
      conditions: [
        "Invoice date is within the selected date range",
        "Status: Approved, Partially Collected, or Collected",
        "Filtered by selected currency",
      ],
      description:
        "Total weight (in kilograms) of goods sold within the current selected period.",
    },
  };

  const widgetList = [
    {
      // Column 1
      widgetOneTitle: "Last Cutoff Receivable",
      widgetOneAmount: widget.lastCutoffReceivable,
      // Column 2
      widgetTwoTitle: "Current Sales Total",
      widgetTwoAmount: widget.currentSalesTotal,
    },
    {
      // Column 1
      widgetOneTitle: "Total Discount",
      widgetOneAmount: widget.totalDiscount,
      // Column 2
      widgetTwoTitle: "Re-Open",
      widgetTwoAmount: widget.reopen,
    },
    {
      // Column 1
      widgetOneTitle: "Current Cutoff Receivable",
      widgetOneAmount: widget.currentCutoffReceivable,
      // Column 2
      widgetTwoTitle: "Sales Collection",
      widgetTwoAmount: widget.collection,
    },
    {
      // Column 1
      widgetOneTitle: "Previous Month Sales Kilo",
      widgetOneAmount: widget.previousSalesKilo,

      widgetTwoTitle: "Current Month Sales Kilo",
      widgetTwoAmount: widget.currentSalesKilo,
    },
  ];

  const [pendingCount, setPendingCount] = useState(0);
  const [clearFilterSignal, setClearFilterSignal] = useState(0);
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [displayCutOff, setDisplayCutOff] = useState({ from: null, to: null });

  // Fetch ALL pending count — no date range, uses dedicated endpoint
  const fetchAllPendingCount = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/invoice/getPendingCount`);
      setPendingCount(res.data.count ?? 0);
    } catch (error) {
      console.error("Failed to fetch pending count:", error);
    }
  }, []);

  useEffect(() => {
    fetchAllPendingCount();
  }, [fetchAllPendingCount]);

  const statusProps = {
    selectedCutOff,
    setSelectedCutOff,
    currencyId,
    fetchCutOff,
    userLoggedID,
    authrztn,
    setPendingCount,
    fetchAllPendingCount, // allows child tabs to refresh the badge count after approve/delete
    clearFilterSignal, // incremented by handleClearFilter so child tabs reset search/customer/column
  };

  useEffect(() => {
    fetchCutOff();
  }, []);

  // "pdf" | "excel" while the export options modal is open, else null
  const [exportFormat, setExportFormat] = useState(null);
  const pdfPreview = usePdfPreview();

  const handleDateRangeChange = (startDate, endDate) => {
    const cutoff = {
      id: "custom",
      from: startDate,
      to: endDate,
    };
    setSelectedCutOff(cutoff);
    setDisplayCutOff({ from: startDate, to: endDate });
    fetchWidgets(cutoff);
    fetchLastCutoffReceivable(cutoff);
    FilterStore.save(startDate, endDate); //// PART OF FILTER RETENTION
    FilterStore.NIclearAllData(); // user picked a real date, exit "show all" mode
  };

  const handleClearFilter = () => {
    // Mark "show all" in localStorage so remount also fetches without date filter.
    FilterStore.NIsaveAllData();
    FilterStore.save(null, null); // clear any saved date range

    const allCutoff = { id: "All", from: null, to: null };
    setSelectedCutOff(allCutoff); // null dates — fetching shows all data

    fetchWidgets(allCutoff);
    fetchLastCutoffReceivable(allCutoff);

    // Reset picker display to default month boundaries — does NOT affect fetching.
    const boundaries = getMonthBoundaries();
    const defaultCutoff = initializeCutoff(boundaries);
    setDisplayCutOff({ from: defaultCutoff.from, to: defaultCutoff.to });
    setDatePickerKey((n) => n + 1);

    // Signal child tabs to reset their search/customer/filterColumn state.
    setClearFilterSignal((n) => n + 1);
  };

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      ) : authrztn.includes("Invoices-View") ? (
        <>
          <div className="w-100 p-2 mb-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">SALES | INVOICE</span>
            </div>

            <div className="d-flex flex-row">
              {/* Export Pdf and excel */}
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-success dropdown-toggle pt-2 px-3 me-2"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Export
                </button>
                <ul className="dropdown-menu">
                  <li
                    className="dropdown-item border-bottom"
                    style={{ cursor: "pointer" }}
                    onClick={() => setExportFormat("pdf")}
                  >
                    PDF
                  </li>
                  <li
                    className="dropdown-item"
                    style={{ cursor: "pointer" }}
                    onClick={() => setExportFormat("excel")}
                  >
                    Excel
                  </li>
                </ul>
              </div>
              {userLoggedID === "11111111-1111-1111-1111-111111111111" && (
                <InvoiceReOpenModal />
              )}
              {authrztn.includes("Invoices-Add") && (
                <Link
                  to="/sales/create-invoice"
                  className="btn btn-primary d-flex flex-row align-items-center title-button"
                >
                  <i className="bx bx-plus fs-5"></i> Create
                </Link>
              )}
            </div>
          </div>
          <CurrencySelector
            setWidgetCurrencySymbol={setWidgetCurrencySymbol}
            currencyId={currencyId}
            setCurrencyId={setCurrencyId}
            style={{ maxWidth: "8.5rem" }}
          />
          <div className="position-relative container-fluid">
            {/* Widgets */}
            <div className="mt-3 mb-4" ref={containerRef}>
              {widgetList.map((item, index) => (
                <div
                  className="d-flex mb-3"
                  key={index}
                  ref={(el) => (containerItemRefs.current[index] = el)}
                >
                  {/* Widget Column #1 */}
                  <div className="col-sm w-100 ps-3 pe-2 payable-card">
                    <div
                      className="d-flex align-items-center justify-content-between w-100 border p-3 shadow-sm rounded hover-card"
                      style={{
                        cursor: "pointer",
                        transition: "transform 0.2s",
                      }}
                      onClick={
                        item.widgetOneTitle === "Last Cutoff Receivable"
                          ? handleShow
                          : undefined
                      }
                    >
                      <div className="d-flex flex-row align-items-center gap-2 text-secondary">
                        <i class="bx bx-bar-chart-alt fs-4"></i>
                        <h3 className="fs-6 mb-0">
                          {item.widgetOneTitle}
                          <WidgetInfoIcon
                            title={item.widgetOneTitle}
                            widgetInfo={widgetInfo}
                          />
                        </h3>
                      </div>

                      <div className="d-flex text-nowrap payable-card-desc">
                        <h1
                          className="text-nowrap mb-0 fs-5 fw-bold"
                          style={{
                            color: "var(--widget-number-color-orange, orange)",
                            fontSize: "2rem",
                            cursor: "pointer",
                          }}
                          title={formatToTwoDecimal(item.widgetOneAmount)}
                        >
                          {!item.widgetOneTitle?.includes("Kilo") &&
                            widgetCurrencySymbol}{" "}
                          {formatToTwoDecimal(item.widgetOneAmount)}
                        </h1>
                      </div>
                    </div>
                  </div>

                  {/* Widget Column #2 */}
                  <div
                    className={`col-sm w-100 px-2 payable-card ${
                      !item.widgetTwoTitle && "d-none"
                    }`}
                  >
                    <div
                      className="d-flex align-items-center justify-content-between w-100 border p-3 shadow-sm rounded hover-card"
                      style={{
                        cursor: "pointer",
                        transition: "transform 0.2s",
                      }}
                    >
                      <div className="d-flex gap-2 flex-row align-items-center text-secondary">
                        <i class="bx bx-money-withdraw fs-4"></i>
                        <h3 className="fs-6 mb-0">
                          {item.widgetTwoTitle}
                          <WidgetInfoIcon
                            title={item.widgetTwoTitle}
                            widgetInfo={widgetInfo}
                          />
                        </h3>
                      </div>

                      <div className="d-flex flex-column text-nowrap payable-card-desc">
                        <h1
                          className="text-nowrap mb-0 fs-5 fw-bold"
                          style={{
                            color: "var(--widget-number-color, #28D120)",
                            fontSize: "2rem",
                            cursor: "pointer",
                          }}
                          title={formatToTwoDecimal(item.widgetTwoAmount)}
                        >
                          {!item.widgetTwoTitle?.includes("Kilo") &&
                            widgetCurrencySymbol}{" "}
                          {formatToTwoDecimal(item.widgetTwoAmount)}
                        </h1>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {activeTab === "approved" && (
              <div className="d-flex flex-row align-items-end gap-2 mt-2 mb-2">
                <DateRangePicker
                  key={datePickerKey}
                  // startDate={selectedCutOff?.from}
                  // endDate={selectedCutOff?.to}
                  startDate={displayCutOff?.from}
                  endDate={displayCutOff?.to}
                  onDateRangeChange={handleDateRangeChange}
                  label="Date Range"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={handleClearFilter}
                  title="Reset date range and reload all data"
                >
                  <i className="bx bx-x-circle"></i>
                  Clear Filter
                </button>
              </div>
            )}
            <div className="container-fluid mt-3">
              <RefreshContext.Provider value={statusProps}>
                <Tabs
                  transition={false}
                  mountOnEnter
                  unmountOnExit={false}
                  id="status-tabs"
                  className="mb-3"
                  activeKey={activeTab} // Use the state here FILTER RETENTION
                  onSelect={handleTabSelect} // Save to store on click FILTER RETENTION
                >
                  <Tab
                    eventKey="pending"
                    title={
                      <span>
                        Pending{" "}
                        {pendingCount > 0 && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#000",
                              color: "#fff",
                              borderRadius: "999px",
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                              minWidth: "1.4em",
                              height: "1.4em",
                              padding: "0 0.4em",
                              lineHeight: 1,
                              verticalAlign: "middle",
                              marginLeft: "4px",
                            }}
                          >
                            {pendingCount}
                          </span>
                        )}
                      </span>
                    }
                  >
                    <PendingStatusTab />
                  </Tab>
                  <Tab eventKey="approved" title="Approved For">
                    <ApprovedStatusTab />
                  </Tab>
                  <Tab eventKey="returned" title="Returned">
                    <ReturnedStatusTab />
                  </Tab>
                </Tabs>
              </RefreshContext.Provider>
            </div>
          </div>

          {/* Last Cutoff Receivable Modal */}
          <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            size="xl"
          >
            <Modal.Header closeButton>
              <Modal.Title>Last Cutoff Receivable</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {/* Search bar */}
              <div className="mb-3 d-flex align-items-center gap-2">
                <div className="input-group" style={{ maxWidth: "360px" }}>
                  <span className="input-group-text bg-white border-end-0">
                    <i className="bx bx-search text-secondary"></i>
                  </span>
                  <input
                    ref={modalSearchRef}
                    type="text"
                    className="form-control border-start-0 ps-0"
                    placeholder="Search by ID, destination, customer…"
                    value={modalSearch}
                    onChange={(e) => setModalSearch(e.target.value)}
                    autoFocus
                  />
                  {modalSearch && (
                    <button
                      className="btn btn-outline-secondary border-start-0"
                      type="button"
                      onClick={() => setModalSearch("")}
                      title="Clear search"
                    >
                      <i className="bx bx-x"></i>
                    </button>
                  )}
                </div>
                {lastCutoffReceivablePagination.totalItems != null && (
                  <span className="text-muted small">
                    {lastCutoffReceivablePagination.totalItems} record
                    {lastCutoffReceivablePagination.totalItems !== 1 ? "s" : ""}
                    {modalSearch.trim() ? " found" : ""}
                  </span>
                )}
              </div>

              <DataTable
                columns={modalColumns}
                data={lastCutoffReceivablePagination.data}
                customStyles={customStyles}
                className="dataTable"
              />
              <PaginationControls {...lastCutoffReceivablePagination} />
            </Modal.Body>
          </Modal>

          <ExportOptionsModal
            exportFormat={exportFormat}
            onHide={() => setExportFormat(null)}
            config={invoiceExportConfig}
            filters={{ currencyId }}
            defaultRange={displayCutOff}
            onPdfReady={pdfPreview.open}
          />
          <PdfPreviewModal {...pdfPreview.modalProps} />
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}
    </div>
  );
};

export const RefreshContext = createContext(0);

export default NewInvoice;
